import { merge } from 'lodash-es';

import { DEFAULT_FORMATTER } from '../../shared/constants.js';
import { defaultConfig } from '../types/default.js';
import {
  multiMeterBarChartTooltipVariantOptions,
  MultiMeterBarChartSlots,
  MultiMeterBarChartConsumerConfig,
  type MultiMeterBarChartTooltipVariant,
} from '../types/multi-meter-bar-chart-config.js';
import { MultiMeterBarChartInternalConfig } from '../types/multi-meter-bar-chart-internal.js';
import { MultiMeterBarChartProps } from '../types/multi-meter-bar-chart.js';

const getMaxValue = (
  totalValue: number,
  maxProperty?: number | 'auto',
  consumerConfigMax?: number,
) => {
  if (typeof maxProperty === 'number') {
    return maxProperty;
  }

  if (maxProperty === 'auto') {
    return totalValue;
  }

  if (!maxProperty && consumerConfigMax) {
    return consumerConfigMax;
  }

  return totalValue;
};

const isValidTooltipVariant = (
  tooltipVariant: MultiMeterBarChartTooltipVariant,
): tooltipVariant is MultiMeterBarChartTooltipVariant =>
  multiMeterBarChartTooltipVariantOptions.includes(tooltipVariant);

const getTooltipConfig = (
  slots: MultiMeterBarChartSlots,
  consumerConfig: MultiMeterBarChartConsumerConfig,
) => {
  const tooltip = merge(
    {},
    defaultConfig.tooltip,
    consumerConfig.tooltip,
    slots.tooltip,
  );
  const variant = isValidTooltipVariant(tooltip.variant)
    ? tooltip.variant
    : defaultConfig.tooltip.variant;

  return {
    ...tooltip,
    variant,
  };
};

export const buildMultiMeterBarChartConfig = (
  props: MultiMeterBarChartProps,
  consumerConfig: MultiMeterBarChartConsumerConfig,
  slots: MultiMeterBarChartSlots,
): MultiMeterBarChartInternalConfig => {
  const size = props.size ?? consumerConfig.size ?? defaultConfig.size;

  const colorPalette =
    props.colorPalette ??
    consumerConfig.colorPalette ??
    defaultConfig.colorPalette;

  const min = props.min ?? consumerConfig.min ?? defaultConfig.min;

  const tooltip = getTooltipConfig(slots, consumerConfig);

  const formatter =
    props.formatter ?? consumerConfig.formatter ?? DEFAULT_FORMATTER;

  const legend = slots.legend ?? consumerConfig.legend ?? defaultConfig.legend;

  const totalValue = slots.segments.reduce(
    (acc, segment) => acc + segment.value,
    0,
  );

  const max = getMaxValue(totalValue, props.max, consumerConfig.max);

  return {
    segments: slots.segments,
    colorPalette,
    size,
    min,
    max,
    tooltip,
    iconSlot: slots.icon,
    labelSlot: slots.label,
    valueSlot: slots.value,
    minSlot: slots.min,
    maxSlot: slots.max,
    legend,
    formatter,
    totalValue,
  };
};
